import type { ComponentPropsWithoutRef, JSX, SyntheticEvent } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { BorderRadius } from '../../css/designTokens/borderRadius';

const getFillStyle = (fillContainer: boolean | string): string | undefined => {
  if (fillContainer === 'horizontal') {
    return 'width: 100%;';
  }
  if (fillContainer === 'vertical') {
    return 'height: 100%;';
  }
  if (fillContainer === true) {
    return `
      width: 100%;
      height: 100%;
    `;
  }
  return undefined;
};

export type StyledImageProps = {
  $fillContainer: boolean | 'horizontal' | 'vertical';
  $objFit?: string | undefined;
  $objPosition?: string | undefined;
  $borderRadius: BorderRadius | undefined;
};

const StyledImage = styled.img<StyledImageProps>`
  border-radius: ${({ $borderRadius }) =>
    isNotNil($borderRadius) ? `var(--wui-${$borderRadius})` : undefined};
  ${({ $fillContainer }) => getFillStyle($fillContainer)};
  object-fit: ${({ $objFit }) => $objFit};
  object-position: ${({ $objPosition }) => $objPosition};
`;

export type ImageProps = ComponentPropsWithoutRef<'img'> & {
  /**
   * Accessible text to apply to the image
   */
  alt: string;
  /**
   * Border radius to apply to the image
   */
  borderRadius?: BorderRadius;
  /**
   * Specifies whether the width and/or height should fill the container
   *
   * *Note: this will override height/width attributes*
   */
  fill?: boolean | 'horizontal' | 'vertical';
  /**
   * Specifies how the image's intrinsic size (ie. its natural size) should
   * be adjusted to fit within its container
   */
  fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /**
   * Indicates how the browser should load the image; `eager`loads the image immediately,
   * `lazy` defers loading the image until it reaches a calculated distance from the viewport.
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
   */
  loading?: 'eager' | 'lazy';
  /**
   * Specifies how the image will align itself in its container
   */
  position?: string;
  /**
   * URL to image file
   */
  src: string;
  /**
   * A callback that is invoked after the image has loaded
   */
  onLoad?: (event?: SyntheticEvent<HTMLImageElement>) => void;
  /**
   * A callback that is invoked after the image has failed to load
   */
  onError?: (event?: SyntheticEvent<HTMLImageElement>) => void;
};

export const Image = ({
  src,
  alt,
  borderRadius,
  fill: fillContainer = false,
  fit: objFit,
  loading = 'lazy',
  position: objPosition,
  onLoad,
  onError,
  ...props
}: ImageProps): JSX.Element => (
  <StyledImage
    $borderRadius={borderRadius}
    $fillContainer={fillContainer}
    $objFit={objFit}
    $objPosition={objPosition}
    alt={alt}
    loading={loading}
    onError={onError}
    onLoad={onLoad}
    src={src}
    {...props}
  />
);

Image.displayName = 'Image';
